import { useState, useEffect } from 'react';
import { useUserData } from '@/context/UserDataContext';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import { DEMO_RECOMMENDATIONS } from '@/data/demoRecommendations';

const FORCE_DEMO = import.meta.env.VITE_DEMO_MODE === 'true';

// Helper function to transform frequency options to backend format
function transformFrequency(freq: string): 'low' | 'moderate' | 'high' {
  switch (freq) {
    case 'very_low':
    case 'never':
    case 'once_a_week':
      return 'low';
    case 'two_three_times_a_week':
      return 'moderate';
    case 'four_plus_times_a_week':
      return 'high';
    default:
      return 'moderate';
  }
}

// Helper function to transform consumption frequency to backend format
function transformConsumption(freq: string): 'never' | 'rarely' | 'moderate' | 'frequent' {
  switch (freq) {
    case '':
    case 'never':
      return 'never';
    case 'once_a_week':
      return 'rarely';
    case 'two_three_times_a_week':
      return 'moderate';
    case 'four_plus_times_a_week':
      return 'frequent';
    default:
      return 'moderate';
  }
}

// Helper function to try both production URLs
async function fetchWithFallback(endpoint: string, options: RequestInit) {
  const isProd = import.meta.env.PROD;
  const primaryUrl = import.meta.env.VITE_API_URL;
  const fallbackUrl = 'https://allera-production.up.railway.app';

  try {
    // In development, just use the development URL
    if (!isProd) {
      const response = await fetch(`${primaryUrl}${endpoint}`, options);
      return response;
    }

    // In production, try primary URL first
    try {
      const response = await fetch(`${primaryUrl}${endpoint}`, options);
      if (response.ok) return response;
    } catch (error) {
      console.log('Primary URL failed, trying fallback...');
    }

    // If primary fails, try fallback
    const response = await fetch(`${fallbackUrl}${endpoint}`, options);
    return response;
  } catch (error) {
    throw new Error('Failed to fetch from both URLs');
  }
}

export interface Recommendation {
  id: string;
  supplementId: string;
  name: string;
  description: string;
  full_description: string;
  priority: 'high' | 'medium' | 'low';
  reason: string;
  notes: string;
  tags: string[];
  dosage: string;
  dosage_male: string;
  dosage_female: string;
  how_to_take: string;
  how_to_take_simple: string;
  precautions: string;
  type: string;
  rating: number | null;
  benefits: string[];
  benefitsLong: Record<string, string>;
  matchConfidence: number;
  matchesGoals?: string[];
}

interface BackendRecommendation {
  supplementId: string;
  priority: 'high' | 'medium' | 'low';
  reason: string;
  notes: string;
  tags: string[];
  matchConfidence: number;
}

export const useRecommendations = () => {
  const { userData, isDataComplete } = useUserData();
  const [recommendations, setRecommendations] = useState<Recommendation[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (FORCE_DEMO && isDataComplete) {
      setRecommendations(DEMO_RECOMMENDATIONS);
      return;
    }

    const fetchRecommendations = async () => {
      if (!isDataComplete) {
        return;
      }
      
      setIsLoading(true);
      setError(null);
      
      try {
        // Send the data directly without transformation
        const dataToSend = {
          age: userData.age || 0,
          gender: userData.gender || 'other',
          goals: userData.goals,
          healthConcerns: userData.healthConcerns,
          currentSupplements: userData.currentSupplements,
          exerciseFrequency: userData.exerciseFrequency || 'never',
          exerciseType: userData.exerciseType,
          stressLevel: userData.stressLevel || 'moderate',
          alcoholConsumption: userData.alcoholConsumption || 'never',
          smoking: userData.smoking ?? false,
          meatConsumption: userData.meatConsumption || 'never',
          seafoodConsumption: userData.seafoodConsumption || 'never',
          dairyConsumption: userData.dairyConsumption || 'never',
          dangerMode: userData.dangerMode ?? false
        };

        // Get personalized recommendations from backend using the new helper
        const response = await fetchWithFallback('/api/recommendations', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(dataToSend)
        });

        const data = await response.json();
        
        if (!response.ok) {
          throw new Error(data.error || 'Failed to fetch recommendations');
        }

        if (!data.recommendations || data.recommendations.length === 0) {
          setError('No recommendations found based on your input.');
          toast.error('No recommendations found');
          return;
        }

        const backendRecommendations = data.recommendations as BackendRecommendation[];
        const supplementIds = backendRecommendations.map(rec => rec.supplementId);

        // 2. Get static supplement data from Supabase
        const { data: supplementData, error: supplementError } = await supabase
          .from('supplements')
          .select(`
            id,
            name,
            description,
            full_description,
            dosage_male,
            dosage_female,
            how_to_take,
            how_to_take_simple,
            precautions,
            type,
            rating
          `)
          .in('id', supplementIds);

        if (supplementError) {
          console.error('Error fetching supplement data:', supplementError);
          throw new Error('Failed to fetch supplement details');
        }

        // 3. Get benefits data from Supabase
        const { data: benefitsData, error: benefitsError } = await supabase
          .from('supplement_benefits')
          .select('supplement_id, benefit, benefit_long')
          .in('supplement_id', supplementIds);

        if (benefitsError) {
          console.error('Error fetching benefits data:', benefitsError);
          throw new Error('Failed to fetch supplement benefits');
        }

        // Create maps for static data
        const supplementMap = (supplementData || []).reduce((acc, supp) => {
          acc[supp.id] = supp;
          return acc;
        }, {} as Record<string, any>);

        // Create benefits map
        const benefitsMap = (benefitsData || []).reduce((acc, item) => {
          if (!acc[item.supplement_id]) {
            acc[item.supplement_id] = {
              benefits: [],
              benefitsLong: {}
            };
          }
          acc[item.supplement_id].benefits.push(item.benefit);
          acc[item.supplement_id].benefitsLong[item.benefit] = item.benefit_long;
          return acc;
        }, {} as Record<string, { benefits: string[], benefitsLong: Record<string, string> }>);

        // 4. Combine backend recommendations with static data
        const transformedRecommendations = backendRecommendations.map(rec => {
          const staticData = supplementMap[rec.supplementId] || {};
          const benefitsData = benefitsMap[rec.supplementId] || { benefits: [], benefitsLong: {} };
          
          return {
            ...rec,
            id: rec.supplementId,
            name: staticData.name || '',
            description: staticData.description || '',
            full_description: staticData.full_description || '',
            dosage: staticData.dosage || '',
            dosage_male: staticData.dosage_male || '',
            dosage_female: staticData.dosage_female || '',
            how_to_take: staticData.how_to_take || '',
            how_to_take_simple: staticData.how_to_take_simple || '',
            precautions: staticData.precautions || '',
            type: staticData.type || '',
            rating: staticData.rating || null,
            benefits: benefitsData.benefits,
            benefitsLong: benefitsData.benefitsLong,
            matchesGoals: []
          };
        });
        
        setRecommendations(transformedRecommendations);
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Failed to generate recommendations';
        setError(message);
        toast.error(message);
      } finally {
        setIsLoading(false);
      }
    };

    fetchRecommendations();
  }, [userData, isDataComplete]);

  return { recommendations, isLoading, error };
};
